import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TeamLogo from "../../components/TeamLogo";
import PlayerNameCell from "../../components/PlayerNameCell";
import { useGetNflTeamPlayersQuery } from "../../store/api/nflApi";
import { getNflFranchise } from "../../utils/nflFranchises";
import {
  hasCurrentNflRoster,
  visibleNflTeamPlayers,
} from "../../utils/nflTeamRoster";
import { QUERY_PAGE_SIZE_OPTIONS } from "../../utils/queryBuilder";
import { NAME_SORT_ORDER, getResultsGridSx, sortedClassFor } from "../../utils/resultsGrid";

const NflTeamDetails = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const franchiseId = String(teamId || "").toUpperCase();
  const franchise = getNflFranchise(franchiseId);
  const { data, isLoading, isError } = useGetNflTeamPlayersQuery(franchiseId, {
    skip: !franchise,
  });
  const [tab, setTab] = useState("roster");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState([
    { field: "playerName", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const sortedField = sortModel[0]?.field;
  const hasCurrent = hasCurrentNflRoster(data?.players);
  const rosterYear = data?.rosterYear;

  const players = useMemo(() => {
    const rows = visibleNflTeamPlayers(data?.players || [], tab);
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((player) => {
      const name = String(player.playerName || "").toLowerCase();
      const id = String(player.playerId || "").toLowerCase();
      const position = String(player.position || "").toLowerCase();
      return (
        name.includes(needle) ||
        id.includes(needle) ||
        position.includes(needle)
      );
    });
  }, [data?.players, searchTerm, tab]);

  const columns = useMemo(
    () => [
      {
        field: "playerName",
        headerName: "Player",
        flex: 1,
        minWidth: 220,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            sport="nfl"
          />
        ),
      },
      {
        field: "position",
        headerName: "Pos",
        width: 80,
        headerClassName: sortedClassFor("position", sortedField),
        cellClassName: sortedClassFor("position", sortedField),
      },
      {
        field: "lastSeason",
        headerName: "Last",
        width: 90,
        type: "number",
        headerClassName: sortedClassFor("lastSeason", sortedField),
        cellClassName: sortedClassFor("lastSeason", sortedField),
      },
    ],
    [sortedField]
  );

  if (!franchise) {
    return (
      <Box sx={{ py: 2, maxWidth: 960, mx: "auto" }}>
        <Alert severity="warning">Unknown NFL franchise.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/app/nfl/teams")}
          sx={{ mt: 2 }}
        >
          All teams
        </Button>
      </Box>
    );
  }

  const aliasNames = (franchise.aliases || []).filter(
    (name) => name && name !== franchise.name
  );

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 1200, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/app/nfl/teams")}
        sx={{ mb: 2 }}
      >
        All teams
      </Button>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <TeamLogo
          sport="nfl"
          franchiseId={franchise.id}
          teamName={franchise.name}
          size={72}
        />
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {franchise.name}
          </Typography>
          {aliasNames.length > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Also: {aliasNames.join(", ")}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {isError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load the roster from player seasons.
        </Alert>
      ) : null}

      {!isLoading && !isError && !hasCurrent ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Latest ingested seasons do not include a current roster — showing
          everyone who played for this franchise.
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, next) => {
          setTab(next);
          setPaginationModel((current) => ({ ...current, page: 0 }));
        }}
        sx={{ mb: 2 }}
      >
        <Tab
          value="roster"
          label={rosterYear ? `${rosterYear} roster` : "Roster"}
        />
        <Tab value="all" label="All players" />
      </Tabs>

      <TextField
        size="small"
        label="Search players"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        sx={{ mb: 2, minWidth: 240 }}
      />

      <Paper sx={{ overflow: "hidden" }}>
        <DataGrid
          rows={players.map((player) => ({
            id: player.playerId,
            ...player,
          }))}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          disableColumnMenu
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowHeight={52}
          onRowClick={(params) =>
            navigate(`/app/nfl/player/${params.row.playerId}`)
          }
          sx={{
            ...getResultsGridSx(),
            cursor: "pointer",
          }}
        />
      </Paper>
    </Box>
  );
};

export default NflTeamDetails;
